import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Globe, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Valoriza logo (compact version)
const ValorizaMark = () => (
  <svg width="28" height="28" viewBox="50 50 160 160" xmlns="http://www.w3.org/2000/svg">
    <style>{`.m1{fill:#8DC21F}.m2{fill:#5A8C1A}`}</style>
    <path className="m1" d="M123.05,99.03v-41.9H109.1v27.94L89.35,65.31l-9.86,9.86l29.63,29.63C113.08,101.75,117.85,99.7,123.05,99.03" />
    <path className="m2" d="M103.85,110.07L74.22,80.44l-9.86,9.86l19.75,19.75H56.17v13.95h41.91C98.75,118.8,100.8,114.03,103.85,110.07" />
    <path className="m1" d="M155.48,124.01h41.91v-13.95h-27.94l19.75-19.75l-9.86-9.86l-29.63,29.63C152.76,114.03,154.81,118.8,155.48,124.01" />
    <path className="m2" d="M144.44,104.8l29.63-29.63l-9.86-9.86l-19.75,19.75V57.13h-13.95v41.91C135.71,99.7,140.48,101.75,144.44,104.8" />
    <path className="m1" d="M98.08,131.46H56.17v13.95H84.1l-19.75,19.75l9.86,9.86l29.64-29.64C100.8,141.43,98.75,136.66,98.08,131.46" />
    <path className="m2" d="M149.7,145.39l29.64,29.64l9.86-9.86l-19.75-19.75h27.94v-13.95h-41.91C154.81,136.66,152.76,141.43,149.7,145.39" />
    <path className="m2" d="M109.12,150.66L79.49,180.3l9.86,9.86l19.75-19.75v27.94h13.95v-41.92C117.85,155.76,113.08,153.71,109.12,150.66" />
    <path className="m1" d="M130.51,156.43v41.92h13.95v-27.94l19.75,19.75l9.86-9.86l-29.64-29.64C140.48,153.71,135.71,155.76,130.51,156.43" />
  </svg>
);

export default function Footer() {
  const yr = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <footer
      data-testid="footer"
      style={{ background: '#000', borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div className="gradient-line w-full" />
      <div className="v-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <ValorizaMark />
              <div>
                <div className="text-sm font-bold text-white leading-tight">Valoriza</div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#8DC21F' }}>Innovation</div>
              </div>
            </div>
            <p className="text-sm text-white/40 leading-relaxed max-w-[40ch] mb-4">{t('footer.tagline')}</p>
            <a href="mailto:innovation@valorizasm.com" className="inline-flex items-center gap-2 text-sm font-medium hover:text-[#8DC21F]" style={{ color: 'rgba(255,255,255,0.5)', transition: 'color 0.2s' }}>
              <Mail size={14} />innovation@valorizasm.com
            </a>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/35 font-bold mb-4">{t('footer.innovation')}</h4>
            <ul className="space-y-2">
              {[
                { label: t('footer.projects'), href: '/proyectos' },
                { label: t('nav.team'), href: '/equipo' },
                { label: t('footer.challenges'), href: '/retos' },
                { label: t('footer.contact'), href: '/contacto' },
              ].map((l) => (
                <li key={l.label}>
                  <Link to={l.href} className="text-sm text-white/45 hover:text-white/80" style={{ transition: 'color 0.2s' }}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/35 font-bold mb-4">Links</h4>
            <ul className="space-y-2">
              {[
                { label: 'valorizasm.com', href: 'https://valorizasm.com', ext: true },
                { label: 'LinkedIn', href: 'https://es.linkedin.com/company/valorizasm', ext: true },
              ].map((l) => (
                <li key={l.label}>
                  <a href={l.href} target={l.ext ? '_blank' : undefined} rel={l.ext ? 'noopener noreferrer' : undefined} className="inline-flex items-center gap-1.5 text-sm text-white/45 hover:text-white/80" style={{ transition: 'color 0.2s' }}>
                    {l.label} {l.ext && <ExternalLink size={10} />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs text-white/25">© {yr} {t('footer.copyright')}</p>
          <div className="flex items-center gap-4">
            <a href="https://valorizasm.com" target="_blank" rel="noopener noreferrer" className="text-white/25 hover:text-white/50 flex items-center gap-1.5 text-xs" style={{ transition: 'color 0.2s' }}>
              <Globe size={11} />valorizasm.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
